<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  labels: {
    type: Array,
    default: () => [],
  },
  selectedLabels: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const searchQuery = ref('');

const filteredLabels = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  return props.labels
    .filter(label => {
      if (!query) return true;
      return label.title.toLowerCase().includes(query);
    })
    .map(label => ({
      ...label,
      isSelected: props.selectedLabels.includes(label.title),
    }))
    .toSorted((a, b) => {
      if (a.isSelected !== b.isSelected) {
        return Number(b.isSelected) - Number(a.isSelected);
      }
      return a.title.localeCompare(b.title);
    });
});

const getLabelBadgeStyle = color => ({
  backgroundColor: `${color}26`,
  outlineColor: color,
});

const onSelectLabel = label => {
  emit('select', label);
};
</script>

<template>
  <div class="flex min-w-[300px] flex-col">
    <div class="sticky top-0 z-10 bg-n-surface-1 pb-2">
      <div class="relative">
        <span
          class="absolute left-3 top-1/2 i-lucide-search size-5 -translate-y-1/2 text-n-slate-11"
        />
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="
            t('CONTACT_PANEL.LABELS.LABEL_SELECT.SEARCH_AND_SELECT')
          "
          class="reset-base h-9 w-full rounded-xl border-none bg-n-alpha-black2 py-2 pl-12 pr-2 text-sm text-n-slate-12 focus:outline-none"
        />
      </div>
    </div>

    <div class="max-h-80 overflow-y-auto">
      <button
        v-for="label in filteredLabels"
        :key="label.id"
        type="button"
        class="flex w-full items-center gap-2 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-n-alpha-2"
        @click.stop="onSelectLabel(label)"
      >
        <span
          class="flex size-5 shrink-0 items-center justify-center rounded-md border text-[13px] transition-colors"
          :class="
            label.isSelected
              ? 'border-n-brand bg-n-brand text-white'
              : 'border-n-strong bg-n-surface-1 text-transparent'
          "
        >
          <span class="i-lucide-check size-3.5" />
        </span>

        <span
          class="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm font-semibold text-n-slate-12 outline outline-1 -outline-offset-1"
          :style="getLabelBadgeStyle(label.color)"
        >
          <span v-if="label.emoji" class="text-base leading-none">
            {{ label.emoji }}
          </span>
          <span class="truncate">{{ label.title }}</span>
        </span>
      </button>

      <div
        v-if="filteredLabels.length === 0"
        class="px-2 py-2 text-sm text-n-slate-11"
      >
        {{ t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') }}
      </div>
    </div>
  </div>
</template>
